import { Lud } from "../model/lud.model";

/**
    Classe que gerencia uma coleção de objetos Lud (Link Unidirecional) organizados por segmentos.

    A classe permite adicionar, remover e buscar Luds em segmentos específicos, garantindo que os intervalos
    não se sobreponham. Também fornece métodos para buscar Luds em intervalos específicos.

    Métodos principais:
    - incluirLud: Adiciona um novo Lud no intervalo especificado.
    - excluirLud: Remove um Lud do intervalo especificado.
    - existeIntervaloParaNovoLud: Verifica se há espaço para um novo Lud entre `inicio` e `fim` no segmento `idx`.
    - buscarLudsNoIntervalo: Busca todos os Luds que se sobrepõem ao intervalo [inicio, fim) no segmento `idx`.
    - buscarLudsNoSegmento: Busca todos os Luds em um segmento específico.
    - buscarProximoLudGlobal: Busca o próximo Lud a partir de uma posição (idx, inicio, fim) em qualquer segmento.
    - obterListaLuds: Obtém uma lista de todos os Luds armazenados.
 */
export class MapaLuds {

    /**
     * Mapa que armazena uma lista de Luds associada ao segmento associado ao número.
     */
    private mapaLuds: Map<number, Lud[]>;

    constructor() {
        this.mapaLuds = new Map();
    }
    
    /**
     * Inclui um objeto Lud no mapa de Luds.
     * 
     * @param lud O objeto Lud a ser incluído.
     * @returns Retorna true se o Lud foi incluído com sucesso, caso contrário retorna false.
     */
    public incluirLud(lud: Lud): boolean {
    
        // Verifica se há espaço disponível antes de incluir
        if (!this.existeIntervaloParaNovoLud(lud.idx, lud.inicio, lud.fim)) {
            return false;
        }
        
        const ludsNoSegmento = this.mapaLuds.get(lud.idx) || [];

        // Insere o Lud na posição correta para manter a lista ordenada por `inicio`
        let inserido = false;
        for (let i = 0; i < ludsNoSegmento.length; i++) {
            if (ludsNoSegmento[i].inicio > lud.inicio) {
                ludsNoSegmento.splice(i, 0, lud);
                inserido = true;
                break;
            }
        }
        if (!inserido) {
            ludsNoSegmento.push(lud);
        }

        this.mapaLuds.set(lud.idx, ludsNoSegmento);
        return true;
    }

    /**
     * Remove um Lud específico do mapa de Luds.
     * @param lud - O Lud a ser removido.
     * @returns True se o Lud foi removido com sucesso, false caso contrário.
     */
    public excluirLud(lud: Lud): boolean {
    
        const ludsNoSegmento = this.mapaLuds.get(lud.idx);

        if (!ludsNoSegmento) return false;

        // Procura o índice da seleção exata
        const index = ludsNoSegmento.findIndex(p => p.inicio === lud.inicio && p.fim === lud.fim);

        if (index === -1) return false; // Seleção não encontrada

        ludsNoSegmento.splice(index, 1); // Remove a seleção

        // Remove o segmento do mapa se não tiver seleções restantes
        if (ludsNoSegmento.length === 0) {
            this.mapaLuds.delete(lud.idx);
        } else {
            this.mapaLuds.set(lud.idx, ludsNoSegmento);
        }

        return true;
    }

    /**
     * Verifica se existe um intervalo disponível para um novo Lud dentro de um segmento específico.
     * 
     * @param idx - O índice do segmento.
     * @param inicio - O início do intervalo.
     * @param fim - O fim do intervalo.
     * @returns Retorna true se o intervalo estiver disponível, caso contrário retorna false.
     */
    public existeIntervaloParaNovoLud(idx: number, inicio: number, fim: number): boolean {

        const ludsNoSegmento = this.mapaLuds.get(idx) || [];

        // Verifica se o intervalo [inicio, fim) não se sobrepõe a nenhum intervalo utilizado
        for (const lud of ludsNoSegmento) {
            if (!(fim <= lud.inicio || inicio >= lud.fim)) {
                return false; // Existe sobreposição
            }
        }

        return true; // Não existe sobreposição
    }

    /**
     * Busca os Luds no intervalo especificado.
     * @param idx O índice do segmento.
     * @param inicio O valor de início do intervalo.
     * @param fim O valor de fim do intervalo.
     * @returns Um array contendo os Luds encontrados no intervalo.
     */
    public buscarLudsNoIntervalo(idx: number, inicio: number, fim: number): Lud[] {
        const ludsNoSegmento = this.mapaLuds.get(idx) || [];
        return ludsNoSegmento.filter(lud => !(lud.fim <= inicio || lud.inicio >= fim));
    }

    /**
     * Busca os Luds no segmento especificado.
     * 
     * @param idx O índice do segmento.
     * @returns Um array contendo os Luds encontrados no segmento especificado.
     */
    public buscarLudsNoSegmento(idx: number): Lud[] {
        return this.mapaLuds.get(idx) || [];
    }

    /**
     * Busca o próximo Lud global com base nos parâmetros fornecidos.
     * Se não encontrar após o intervalo especificado, começa do primeiro Lud na lista.
     * 
     * @param idx O índice do segmento atual.
     * @param inicio O valor de início para buscar o próximo Lud.
     * @param fim O valor de fim para buscar o próximo Lud.
     * @returns O próximo Lud encontrado ou null se não houver Lud disponível.
     */
    public buscarProximoLudGlobal(idx: number, inicio: number, fim: number): Lud | null {

        // Se o mapa estiver vazio, retorna null
        if (this.mapaLuds.size === 0) {
            return null;
        }

        // Obtém os índices ordenados dos segmentos para navegação sequencial
        const segmentosOrdenados = Array.from(this.mapaLuds.keys()).sort((a, b) => a - b);
        let found = false;

        for (const segIdx of segmentosOrdenados) {
            const ludsNoSegmento = this.mapaLuds.get(segIdx) || [];

            // Se estiver no segmento inicial, busca pelo próximo Lud após `fim`
            for (const lud of ludsNoSegmento) {
                if (segIdx === idx && lud.inicio >= fim) {
                    return lud;
                }
                // Se passar do segmento inicial, considera qualquer Lud como próximo
                if (segIdx > idx || found) {
                    return lud;
                }
            }

            // Marca como encontrado se passou do segmento inicial
            if (segIdx === idx) {
                found = true;
            }
        }

        // Se chegou aqui, reinicia a busca do primeiro segmento para achar o primeiro Lud
        const primeiroSegmento = segmentosOrdenados[0];
        return this.mapaLuds.get(primeiroSegmento)?.[0] || null;
    }

    /**
     * Obtém a lista de Luds, sem ordenação por segmento.
     * @returns A lista de Luds.
     */
    public obterListaLuds(): Lud[] {
        const lista: Lud[] = [];
        this.mapaLuds.forEach((luds) => {
            luds.forEach((lud) => {
                lista.push(lud);
            });
        });
        return lista;
    }

    /**
     * Exibe o conteúdo atual do mapa de Luds.
     */
    public exibirMapa(): void {
        console.log('Mapa de Luds:');
        if (this.mapaLuds.size === 0) {
            console.log('Nenhum Lud armazenado.');
            return;
        }

        this.mapaLuds.forEach((luds, idx) => {
            console.log(`\nSegmento ${idx}:`);
            luds.forEach((lud) => {
                console.log(
                    ` [${lud.inicio}, ${lud.fim}] => ${lud.url || 'N/A'}`
                );
            });
        });
    }
}
